import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, RefreshCw, Check, Loader2 } from "lucide-react";

// Capture un selfie (camera frontale) et renvoie un dataURL JPEG 320px.
// onDone(dataUrl | null) — null si l'utilisateur passe/refuse la camera.
export const SelfieCapture = ({ open, onDone, title = "Photo de controle", description = "Prenez un selfie rapide — il sera horodate et conserve comme preuve de securite." }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setError(""); setPhoto(null); setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setError("Impossible d'acceder a la camera (permission refusee ou indisponible).");
    }
    setStarting(false);
  }, []);

  useEffect(() => {
    if (open) startCamera();
    return stopStream;
  }, [open, startCamera, stopStream]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    const w = 320;
    canvas.width = w;
    canvas.height = Math.round((video.videoHeight / video.videoWidth) * w);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL("image/jpeg", 0.7));
    stopStream();
  };

  const finish = (result) => {
    stopStream();
    setPhoto(null); setError("");
    onDone(result);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(null); }}>
      <DialogContent className="max-w-sm" data-testid="selfie-capture-modal">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2"><Camera size={18} className="text-emerald-600" /> {title}</DialogTitle>
          <DialogDescription className="text-xs">{description}</DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl overflow-hidden bg-black aspect-[4/3] grid place-items-center relative">
          {photo ? (
            <img src={photo} alt="Selfie" className="w-full h-full object-cover" data-testid="selfie-preview" />
          ) : error ? (
            <p className="text-white text-xs text-center px-4" data-testid="selfie-error">{error}</p>
          ) : (
            <>
              <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="selfie-video" />
              {starting && <Loader2 className="animate-spin text-white absolute" />}
            </>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          {photo ? (
            <>
              <button onClick={startCamera} className="btn-secondary inline-flex items-center gap-1 text-sm" data-testid="selfie-retake-btn">
                <RefreshCw size={14} /> Reprendre
              </button>
              <button onClick={() => finish(photo)} className="btn-primary inline-flex items-center gap-1 text-sm" data-testid="selfie-confirm-btn">
                <Check size={14} /> Valider
              </button>
            </>
          ) : (
            <>
              <button onClick={() => finish(null)} className="btn-secondary text-sm" data-testid="selfie-skip-btn">
                {error ? "Continuer sans photo" : "Passer"}
              </button>
              {!error && (
                <button onClick={capture} disabled={starting} className="btn-primary inline-flex items-center gap-1 text-sm" data-testid="selfie-capture-btn">
                  <Camera size={14} /> Capturer
                </button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
